import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle, BookOpen, Package, XCircle, RotateCcw } from "lucide-react";
import PaymentStatusShell from "./PaymentStatusShell";
import { getPaymentQueryData } from "./paymentStatusQuery";
import { verifyEpsRecipePurchase, verifyRecipePurchase } from "../../services/recipe.api";

// Same cadence as the order payment page: EPS only reports a payment once it
// is finished and the API spaces its own lookups, so ask every few seconds for
// a couple of minutes (the server keeps checking after that).
const EPS_POLL_MS = 5000;
const EPS_POLL_FOR_MS = 2 * 60 * 1000;

/**
 * Where a recipe purchase lands after the gateway:
 *   EPS    → /recipes/purchase/{success|fail|cancel}?transactionId=…&status=…&recipeId=…
 *            (via the API, which has already re-checked the purchase with EPS)
 *   Stripe → /recipes/purchase/{success|cancel}?transactionID=…&recipeId=…  (no status)
 * The page never decides on its own that a purchase went through: EPS's
 * status comes from the API, and a Stripe session is confirmed against it.
 */
export default function RecipePurchaseReturn({ outcome }) {
  const [searchParams] = useSearchParams();
  const { status, transactionID, needsVerification } = getPaymentQueryData(searchParams);
  const recipeId = searchParams.get("recipeId");
  const recipeHref = recipeId ? `/recipe-details/${encodeURIComponent(recipeId)}` : "/all-recipes";

  const [stripeStatus, setStripeStatus] = useState(null);

  useEffect(() => {
    if (!needsVerification || outcome !== "success") return;
    let cancelled = false;
    verifyRecipePurchase(transactionID)
      .then((res) => {
        if (!cancelled) setStripeStatus(res?.data?.purchase?.status || "pending");
      })
      .catch(() => {
        // The webhook settles the purchase regardless; don't claim failure.
        if (!cancelled) setStripeStatus("pending");
      });
    return () => {
      cancelled = true;
    };
  }, [needsVerification, outcome, transactionID]);

  // An EPS success return that the API couldn't confirm yet is re-asked of the
  // API (which asks EPS); fail/cancel returns aren't — the buyer left without
  // paying, and the server's sweep still catches a late payment.
  const shouldPollEps = !needsVerification && !!transactionID && outcome === "success" && status !== "paid";
  const [epsStatus, setEpsStatus] = useState(status);
  const [epsPolling, setEpsPolling] = useState(shouldPollEps);

  useEffect(() => {
    if (!shouldPollEps) return;
    let stopped = false;
    let timer;
    const startedAt = Date.now();

    const check = async () => {
      try {
        const res = await verifyEpsRecipePurchase(transactionID);
        const next = res?.data?.purchase?.status;
        if (stopped) return;
        if (next) setEpsStatus(next);
        if (next === "paid") {
          setEpsPolling(false);
          return;
        }
      } catch {
        // Rate-limited or offline — keep the last known status and try again.
      }
      if (stopped) return;
      if (Date.now() - startedAt >= EPS_POLL_FOR_MS) {
        setEpsPolling(false);
        return;
      }
      timer = setTimeout(check, EPS_POLL_MS);
    };

    check();
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, [shouldPollEps, transactionID]);

  const effectiveStatus = needsVerification ? stripeStatus : epsStatus;

  const backToRecipe = (label, icon = <BookOpen size={15} />) => (
    <a href={recipeHref} className="ps-btn ps-btn-primary">
      {icon} {label}
    </a>
  );

  // A paid answer wins whichever return URL the gateway used.
  if (effectiveStatus === "paid") {
    return (
      <PaymentStatusShell
        icon={<CheckCircle size={40} />}
        tone="success"
        badge="Purchase Confirmed"
        title="Recipe Unlocked!"
        subtitle="Your payment went through. The full ingredients and cooking steps are now yours to keep."
        transactionID={transactionID}
        actions={backToRecipe("View Recipe")}
      />
    );
  }

  if (outcome === "cancel") {
    return (
      <PaymentStatusShell
        icon={<Package size={40} />}
        tone="warning"
        badge="Purchase Cancelled"
        title="Purchase Cancelled"
        subtitle="You left the payment page before finishing. Nothing was charged — you can buy the recipe whenever you're ready."
        transactionID={transactionID}
        actions={backToRecipe("Back to Recipe")}
      />
    );
  }

  if (outcome === "fail" || effectiveStatus === "failed") {
    return (
      <PaymentStatusShell
        icon={<XCircle size={40} />}
        tone="danger"
        badge="Payment Failed"
        title="Payment Unsuccessful"
        subtitle="The payment provider has no completed payment for this purchase. If money left your account it will still be matched automatically and the recipe will unlock."
        transactionID={transactionID}
        note="If the issue persists, contact support with your transaction ID."
        actions={backToRecipe("Try Again", <RotateCcw size={15} />)}
      />
    );
  }

  if ((needsVerification && stripeStatus === null) || epsPolling) {
    return (
      <PaymentStatusShell
        icon={<Package size={40} />}
        tone="info"
        badge="Confirming"
        title="Confirming your payment…"
        subtitle="Hang on a moment while we check with the payment provider."
        transactionID={transactionID}
      />
    );
  }

  return (
    <PaymentStatusShell
      icon={<Package size={40} />}
      tone="info"
      badge="Payment Status"
      title="Payment is still being confirmed"
      subtitle="The payment provider hasn't confirmed it yet. We keep checking — the recipe unlocks on its own as soon as it does."
      transactionID={transactionID}
      actions={backToRecipe("Back to Recipe")}
    />
  );
}
